import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import moment from 'moment-timezone';
import { useSelector, useDispatch } from 'react-redux';
import {
  InfoCircleOutlined,
  PlusOutlined,
  SendOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  AutoComplete,
  Input,
  Tooltip,
  Switch,
  Modal,
  Checkbox,
  Button,
  Card,
  Tag,
} from 'antd';
import { showToastSuccess } from 'src/utilities/toast';
import { showToastError } from 'src/utilities/errortoast';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { listSummaries, regenerateSummary } from '../../store/actions/summary';
import { usePolling } from '../../hooks/usePolling';
import { analytics } from 'src/services';
import {
  submitDiagnosesConfig,
  submitDiagnosesToEHR,
  getDiagnosesBySearchString,
  deleteDiagnosesConfig,
} from 'src/lib/requests/ehr.url';
import { updateCurrentPractitionerSettings } from 'src/store/actions/practitioner.action';
import { debounce } from 'lodash';
import { getFromStorage, storeInLocal } from 'src/lib/storage';

const ICD10SettingsToggle = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { currentPractitionerSettings } = useSelector(
    (state) => state?.practitionerState
  );
  const { encounterDetails } = useSelector((state) => state?.encounterState);

  const handlePrefixToggle = (checked) => {
    setIsModalVisible(true);
    if (checked) {
      analytics.track('Enabled include ICD-10 codes in notes', {
        email: user?.email,
      });
    } else {
      analytics.track('Disabled include ICD-10 codes in notes', {
        email: user?.email,
      });
    }
    dispatch(
      updateCurrentPractitionerSettings({
        note_preferences: {
          ...currentPractitionerSettings?.note_preferences,
          prefix_icd10: checked,
        },
      })
    );
  };

  const getCurrentTemplate = () => {
    return {
      template_id: encounterDetails?.in_visit_template_id,
      template_overrides_id: encounterDetails?.in_visit_template_overrides_id,
    };
  };

  const handleRegenerateConfirm = () => {
    analytics.track('Confirm regenerate note with ICD-10 codes', {
      email: user?.email,
    });
    dispatch(
      regenerateSummary(
        encounterDetails?.encounter_id,
        'in-visit',
        '',
        currentPractitionerSettings?.note_preferences?.prefix_icd10
          ? 'Insert ICD-10 codes in the note'
          : 'Remove ICD-10 codes from the note',
        getCurrentTemplate(),
        encounterDetails?.summary_verbosity,
        encounterDetails?.summary_format,
        'update'
      )
    );
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    analytics.track('Cancel regenerate note with ICD-10 codes', {
      email: user?.email,
    });
    setIsModalVisible(false);
  };

  return (
    <>
      <div className="p-4 flex items-center justify-between border-b pb-3">
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-600">
            Include ICD-10 codes in notes
          </div>
          <Tooltip
            placement="bottomLeft"
            overlayStyle={{
              position: 'fixed',
            }}
            title={
              <span>
                When enabled, ICD-10 codes will be <br /> automatically prefixed{' '}
                in the <br /> Assessment section of your notes
              </span>
            }
          >
            <InfoCircleOutlined className="text-gray-400 cursor-help" />
          </Tooltip>
        </div>
        <Switch
          checked={currentPractitionerSettings?.note_preferences?.prefix_icd10}
          onChange={handlePrefixToggle}
          className={`${
            currentPractitionerSettings?.note_preferences?.prefix_icd10
              ? 'bg-[#00D090]'
              : 'bg-gray-200'
          }`}
        />
      </div>

      <Modal
        title="Regenerate note"
        open={isModalVisible}
        onOk={handleRegenerateConfirm}
        onCancel={handleCancel}
        okText="Yes"
        cancelText="No"
      >
        <p>
          Would you like to regenerate the current note to{' '}
          {currentPractitionerSettings?.note_preferences?.prefix_icd10
            ? 'include'
            : 'remove'}{' '}
          ICD-10 codes?
          <div className="flex text-orange-600 text-sm pt-2">
            Note: New encounters from now on will use the updated settings.
          </div>
        </p>
      </Modal>
    </>
  );
};

export const ICD10Coding = forwardRef(function ICD10Coding(
  { encounterStatus, allowed },
  ref
) {
  const { icd10Summary, icd10UpdatedAt } = useSelector(
    (state) => state.summaryState
  );
  const { snomedCodesSummary } = useSelector((state) => state.summaryState);
  const { encounterDetails } = useSelector((state) => state?.encounterState);
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const dispatch = useDispatch();
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  const [isCustomDiagnosisModalVisible, setIsCustomDiagnosisModalVisible] =
    useState(false);
  const [newDiagnosisCode, setNewDiagnosisCode] = useState('');
  const [newDiagnosisDescription, setNewDiagnosisDescription] = useState('');
  const [newDiagnosisInput, setNewDiagnosisInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [newDiagnosisSnomedCode, setNewDiagnosisSnomedCode] = useState('');
  const [newDiagnosisJustification, setNewDiagnosisJustification] =
    useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [currentSearchText, setCurrentSearchText] = useState('');
  const [recentDiagnoses, setRecentDiagnoses] = useState([]);
  const [expandedEvidence, setExpandedEvidence] = useState({});
  const [diagnosisError, setDiagnosisError] = useState(false);
  const [justificationError, setJustificationError] = useState(false);
  const [isSavingCustomDiagnosis, setIsSavingCustomDiagnosis] = useState(false);

  const { isEntitled: canSubmitICD10ToEHR } =
    useFeatureEntitlements('icd10-submission');

  const toggleEvidence = (code) => {
    setExpandedEvidence((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const getConfidenceLevel = (score) => {
    if (score >= 0.8) {
      return {
        label: 'High',
        style: { backgroundColor: '#E6F7F2', color: '#00D090' },
      };
    }
    if (score >= 0.5) {
      return {
        label: 'Medium',
        style: { backgroundColor: '#FFF7E6', color: '#FAAD14' },
      };
    }
    return {
      label: 'Low',
      style: { backgroundColor: '#FFF1F0', color: '#F5222D' },
    };
  };

  const resetCustomDiagnosisModal = () => {
    setIsCustomDiagnosisModalVisible(false);
    setSearchResults([]);
    setNewDiagnosisCode('');
    setNewDiagnosisDescription('');
    setNewDiagnosisInput('');
    setNewDiagnosisSnomedCode('');
    setNewDiagnosisJustification('');
    setPage(1);
    setHasMore(true);
    setCurrentSearchText('');
    setDiagnosisError(false);
    setJustificationError(false);
  };

  const diagnoses = useMemo(() => {
    if (!icd10Summary) {
      return [];
    }
    return icd10Summary.map((icdItem) => {
      const snomedItem = snomedCodesSummary?.find(
        (snomed) => snomed.icd10_code === icdItem.code
      );
      return {
        ...icdItem,
        snomed_code: icdItem.snomed_code || (snomedItem ? snomedItem.code : ''),
      };
    });
  }, [icd10Summary, snomedCodesSummary]);

  useEffect(() => {
    if (icd10Summary) {
      // Initially, all AI-suggested diagnoses are selected.
      setSelectedDiagnoses(icd10Summary.map((s) => s.code));
    }
  }, [icd10Summary]);

  const handleSelectDiagnosis = (code) => {
    setSelectedDiagnoses((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleDeleteDiagnosis = (code) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this diagnosis?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await dispatch(
            deleteDiagnosesConfig(encounterDetails.encounter_id, [{ code }])
          );
          if (result?.status === 200 || result?.status === 204) {
            dispatch(
              listSummaries(encounterDetails.encounter_id, 'in-visit', 'icd-10')
            );
            showToastSuccess('Diagnosis deleted successfully');
          } else {
            showToastError('Failed to delete diagnosis');
          }
        } catch (error) {
          showToastError('Failed to delete diagnosis');
        }
      },
    });
  };

  const fetchAndSetResults = async (searchText, pageNum) => {
    if (!searchText || searchText.length < 3) {
      setSearchResults([]);
      setHasMore(true);
      return;
    }
    setIsSearching(true);
    try {
      const response = await dispatch(
        getDiagnosesBySearchString(
          currentPractitioner.practitioner_id,
          searchText,
          pageNum
        )
      );

      if (response?.data && Array.isArray(response.data)) {
        const newResults = response.data.map((item) => ({
          key: `${item.icd10_code}-${item.snomed_code}`,
          value: `${item.icd10_code} - ${item.description}`,
          label: (
            <Tooltip title={item.target_name} placement="topLeft">
              {`${item.icd10_code} - ${item.description}`}
            </Tooltip>
          ),
          snomed_code: item.snomed_code,
          code: item.icd10_code,
          description: item.description,
        }));

        const filteredResults = newResults.filter(
          (result) =>
            !recentDiagnoses.some((recent) => recent.key === result.key)
        );

        if (pageNum === 1) {
          setSearchResults(filteredResults);
        } else {
          setSearchResults((prev) => [...prev, ...filteredResults]);
        }

        if (response.data.length < 20) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
        setPage(pageNum);
      } else {
        if (pageNum === 1) {
          setSearchResults([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      showToastError('Failed to search diagnoses');
      setHasMore(false);
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchText) => {
      setCurrentSearchText(searchText);
      setPage(1);
      setHasMore(true);
      fetchAndSetResults(searchText, 1);
    }, 300),
    [currentPractitioner?.practitioner_id]
  );

  const handlePopupScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop - clientHeight < 1 &&
      !isSearching &&
      hasMore
    ) {
      fetchAndSetResults(currentSearchText, page + 1);
    }
  };

  const onSelectDiagnosis = (value, option) => {
    setNewDiagnosisInput(value);
    setNewDiagnosisCode(option.code);
    setNewDiagnosisDescription(option.description);
    setNewDiagnosisSnomedCode(option.snomed_code);
    setDiagnosisError(false);
  };

  const handleAddCustomDiagnosis = () => {
    const storedRecents = getFromStorage(
      `recent_icd10_codes_${currentPractitioner.practitioner_id}`
    );
    if (storedRecents) {
      setRecentDiagnoses(JSON.parse(storedRecents));
    }
    setIsCustomDiagnosisModalVisible(true);
  };

  const handleSaveCustomDiagnosis = async () => {
    const isDiagnosisInvalid = !newDiagnosisCode || !newDiagnosisDescription;
    const isJustificationInvalid = !newDiagnosisJustification.trim();

    setDiagnosisError(isDiagnosisInvalid);
    setJustificationError(isJustificationInvalid);

    if (isDiagnosisInvalid || isJustificationInvalid) {
      return;
    }

    setIsSavingCustomDiagnosis(true);
    const newDiagnosis = {
      code: newDiagnosisCode,
      description: newDiagnosisDescription,
      snomed_code: newDiagnosisSnomedCode || undefined,
      note: newDiagnosisJustification,
    };

    try {
      const result = await dispatch(
        submitDiagnosesConfig(encounterDetails.encounter_id, [newDiagnosis])
      );
      if (result?.status == 200 || result?.status == 201) {
        showToastSuccess('Diagnosis added successfully');
        const newRecentItem = {
          key: `${newDiagnosis.code}-${newDiagnosis.snomed_code || ''}`,
          value: `${newDiagnosis.code} - ${newDiagnosis.description}`,
          snomed_code: newDiagnosis.snomed_code,
          code: newDiagnosis.code,
          description: newDiagnosis.description,
        };
        const storedRecentsRaw = getFromStorage(
          `recent_icd10_codes_${currentPractitioner.practitioner_id}`
        );
        const storedRecents = storedRecentsRaw
          ? JSON.parse(storedRecentsRaw)
          : [];
        const updatedRecents = [newRecentItem, ...storedRecents];
        const uniqueRecents = [
          ...new Map(updatedRecents.map((item) => [item.key, item])).values(),
        ];
        storeInLocal(
          `recent_icd10_codes_${currentPractitioner.practitioner_id}`,
          JSON.stringify(uniqueRecents.slice(0, 10))
        );
        setSelectedDiagnoses((prev) => [...prev, newDiagnosis.code]);
        resetCustomDiagnosisModal();
        dispatch(
          listSummaries(encounterDetails.encounter_id, 'in-visit', 'icd-10')
        );
      } else {
        showToastError('Failed to add custom diagnosis. Please try again.');
      }
    } catch (error) {
      showToastError('Failed to add custom diagnosis. Please try again.');
    } finally {
      setIsSavingCustomDiagnosis(false);
    }
  };

  const handleSubmitToEHR = async () => {
    if (!isICD10SubmissionEnabled) {
      return {
        success: true,
        message: 'ICD-10 submission is not enabled for this account.',
      };
    }
    const payload = diagnoses.filter((d) => selectedDiagnoses.includes(d.code));
    if (payload.length === 0) {
      return {
        success: true,
        message: 'No diagnoses selected for submission.',
      };
    }
    try {
      const result = await dispatch(
        submitDiagnosesToEHR(
          encounterDetails.encounter_id,
          encounterDetails.org_uuid,
          payload
        )
      );
      if (result?.status == 200 || result?.status == 201) {
        showToastSuccess('Diagnoses submitted to EHR successfully!');
        analytics.track('Successfully submitted Diagnoses to EHR!', {
          encounter_id: encounterDetails.encounter_id,
        });
        return {
          success: true,
          message: 'Diagnoses submitted to EHR successfully!',
        };
      } else {
        analytics.track('Failed to submit Diagnoses to EHR!', {
          encounter_id: encounterDetails.encounter_id,
        });
        return {
          success: false,
          message: 'Failed to submit diagnoses to EHR. Please try again.',
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Failed to submit diagnoses to EHR. Please try again.',
      };
    }
  };

  const calculateRefreshInterval = useCallback(() => {
    const isGenerating = encounterDetails?.icd_10_status === 'inprogress';
    return isGenerating ? 10000 : 60000;
  }, [encounterDetails?.icd_10_status]);

  const calculateRefreshIntervalSNOMED = useCallback(() => {
    const isGenerating = encounterDetails?.snomed_status === 'inprogress';
    return isGenerating ? 10000 : 60000;
  }, [encounterDetails?.snomed_status]);

  const fetchIcd10Codes = useCallback(() => {
    if (encounterDetails?.encounter_id) {
      dispatch(
        listSummaries(encounterDetails.encounter_id, 'in-visit', 'icd-10')
      );
    }
  }, [dispatch, encounterDetails?.encounter_id]);

  const fetchSNOMEDCodes = useCallback(() => {
    if (encounterDetails?.encounter_id) {
      dispatch(
        listSummaries(encounterDetails.encounter_id, 'in-visit', 'snomed')
      );
    }
  }, [dispatch, encounterDetails?.encounter_id]);

  usePolling(
    {
      pollingFunction: fetchSNOMEDCodes,
      calculateInterval: calculateRefreshIntervalSNOMED,
    },
    [encounterDetails?.snomed_status, encounterDetails?.encounter_id]
  );

  usePolling(
    {
      pollingFunction: fetchIcd10Codes,
      calculateInterval: calculateRefreshInterval,
    },
    [encounterDetails?.icd_10_status, encounterDetails?.encounter_id]
  );

  const autocompleteOptions = useMemo(() => {
    const options = [];

    if (newDiagnosisInput.length === 0 && recentDiagnoses.length > 0) {
      options.push({
        label: 'Recently Used',
        options: recentDiagnoses.slice(0, 3).map((item) => ({
          ...item,
          label: (
            <Tooltip title={item.description} placement="right">
              {item.value}
            </Tooltip>
          ),
        })),
      });
    }

    if (searchResults.length > 0) {
      options.push({
        label: 'Search Results',
        options: searchResults,
      });
    }

    return options;
  }, [recentDiagnoses, searchResults, newDiagnosisInput]);

  const isICD10SubmissionEnabled =
    settingsOrgValue?.org_settings?.is_icd10_submission_enabled &&
    canSubmitICD10ToEHR;

  useImperativeHandle(ref, () => ({
    submitToEHR: handleSubmitToEHR,
  }));

  if (!allowed) {
    return (
      <div className="text-gray-400 p-4 text-xs">
        ICD-10 coding feature is not enabled for this account.
      </div>
    );
  }

  const aiDiagnosesCount = diagnoses.filter((d) => d.confidence).length;
  const manualDiagnosesCount = diagnoses.length - aiDiagnosesCount;

  return (
    <div className="flex flex-col h-full">
      <ICD10SettingsToggle />
      <div className="flex-grow overflow-y-auto p-4">
        {isICD10SubmissionEnabled && (
          <div className="mb-4">
            {/* <div className="flex items-center gap-2 mb-2"> */}
            {/* <h3 className="text-lg font-bold">ICD-10 Diagnosis Codes</h3>
              <div className="flex items-baseline gap-1">
                {(aiDiagnosesCount > 0 || manualDiagnosesCount > 0) && (
                  <Tag
                    style={{
                      backgroundColor: '#E6F7F2',
                      color: '#00D090',
                      border: 'none',
                      fontSize: '11px',
                      padding: '1px 8px',
                      borderRadius: '12px',
                    }}
                  >
                    {aiDiagnosesCount > 0 && `${aiDiagnosesCount} AI`}
                    {aiDiagnosesCount > 0 && manualDiagnosesCount > 0 && ' + '}
                    {manualDiagnosesCount > 0 &&
                      `${manualDiagnosesCount} manual`}
                  </Tag>
                )}
              </div> */}
            {/* </div> */}
            {encounterStatus === 'completed' && (
              <div className="flex justify-end">
                <Button
                  icon={<PlusOutlined />}
                  onClick={handleAddCustomDiagnosis}
                  style={{ color: '#00D090', borderColor: '#00D090' }}
                  className="w-full"
                >
                  Add Code
                </Button>
              </div>
            )}
          </div>
        )}

        {!isICD10SubmissionEnabled &&
          icd10UpdatedAt &&
          encounterDetails?.icd_10_status === 'ready' && (
            <span className="text-gray-400 text-xs flex justify-center mb-2">
              Last updated {moment(icd10UpdatedAt).fromNow()}
            </span>
          )}
        {!isICD10SubmissionEnabled &&
          (encounterStatus !== 'completed' ? (
            <div className="text-gray-400">
              ICD-10 codes will appear shortly after the note is generated.
            </div>
          ) : (
            <div>
              {diagnoses?.map((summary, index) => (
                <Card
                  key={index}
                  title={
                    <span
                      className="text-lg"
                      style={{ color: 'rgb(0, 208, 145)' }}
                    >
                      {summary.code}
                    </span>
                  }
                  className="mb-4"
                >
                  <div className="font-medium" style={{ color: 'black' }}>
                    Description
                  </div>
                  <p
                    className="text-gray-600 text-xs"
                    style={{ marginBottom: '10px' }}
                  >
                    {summary.description}
                  </p>
                  <div className="font-medium" style={{ color: 'black' }}>
                    Evidence
                  </div>
                  <p className="text-gray-600 text-xs">{summary.evidence}</p>
                </Card>
              ))}
              {encounterDetails?.icd_10_status === 'ready' &&
                !diagnoses?.length && (
                  <div className="text-gray-400">
                    No ICD-10 codes found for this encounter.
                  </div>
                )}
              {encounterDetails?.icd_10_status !== 'ready' && (
                <div className="text-gray-400">Extracting ICD-10 codes...</div>
              )}
            </div>
          ))}
        {isICD10SubmissionEnabled && (
          <>
            {/* <div className="mb-4">
              <p className="text-gray-500 text-xs">
                Click on any code to view supporting evidence from clinical
                notes. AI confidence shows how strongly the evidence supports
                each diagnosis.
              </p>
            </div> */}

            {encounterStatus !== 'completed' ? (
              <div className="text-gray-400">
                ICD-10 codes will appear shortly after the note is generated.
              </div>
            ) : (
              <div>
                {[...diagnoses]?.map((summary, index) => {
                  const isSelected = selectedDiagnoses.includes(summary.code);
                  const confidence = summary.confidence
                    ? getConfidenceLevel(summary.confidence)
                    : null;
                  return (
                    <div
                      key={index}
                      onClick={
                        isICD10SubmissionEnabled
                          ? () => handleSelectDiagnosis(summary.code)
                          : undefined
                      }
                      className={`p-3 rounded-lg border-2 mb-2 ${
                        isICD10SubmissionEnabled
                          ? 'cursor-pointer transition-all'
                          : ''
                      } ${
                        isSelected && isICD10SubmissionEnabled
                          ? 'border-green-400 bg-green-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start">
                        {isICD10SubmissionEnabled && (
                          <Checkbox
                            checked={isSelected}
                            className="mr-3 mt-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectDiagnosis(summary.code);
                            }}
                          ></Checkbox>
                        )}
                        <div
                          className="flex-1 min-w-0"
                          onClick={
                            isICD10SubmissionEnabled
                              ? () => handleSelectDiagnosis(summary.code)
                              : undefined
                          }
                        >
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-sm">
                                {summary.code}
                              </span>
                              {!summary.confidence && (
                                <Tag
                                  style={{
                                    backgroundColor: '#F3F0FF',
                                    color: '#5D3FD3',
                                    border: 'none',
                                    fontSize: '11px',
                                    padding: '1px 8px',
                                    borderRadius: '12px',
                                  }}
                                >
                                  Manually Added
                                </Tag>
                              )}
                            </div>
                            <p className="text-gray-700 text-sm">
                              {summary.description}
                            </p>
                          </div>

                          {summary.confidence ? (
                            <div className="flex items-center gap-2 text-[11px] mt-2 mb-2">
                              <div className="w-20 bg-gray-200 rounded-full h-1">
                                <div
                                  className={`h-1 rounded-full`}
                                  style={{
                                    backgroundColor: confidence.style.color,
                                    width: `${summary.confidence * 100}%`,
                                  }}
                                ></div>
                              </div>
                              <span className="text-gray-600 font-semibold">
                                {`${Math.round(summary.confidence * 100)}%`}
                              </span>
                              <Tag
                                style={{
                                  ...confidence.style,
                                  border: 'none',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  padding: '1px 6px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                }}
                              >
                                {confidence.label}
                                <Tooltip title="AI Confidence">
                                  <QuestionCircleOutlined className="cursor-help ml-1" />
                                </Tooltip>
                              </Tag>
                            </div>
                          ) : null}

                          {summary.evidence && (
                            <div>
                              <button
                                onClick={() => toggleEvidence(summary.code)}
                                className="text-[#5a8dda] text-xs flex items-center gap-1"
                              >
                                <span className="underline">
                                  Click to view evidence
                                </span>
                                <span className="ml-1">
                                  {expandedEvidence[summary.code] ? '▲' : '▼'}
                                </span>
                              </button>
                              {expandedEvidence[summary.code] && (
                                <div className="mt-2">
                                  <h4 className="font-semibold text-xs text-gray-600">
                                    Evidence
                                  </h4>
                                  <p className="text-gray-600 text-xs">
                                    {summary.evidence}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                          {summary.note && (
                            <div>
                              <h4 className="font-semibold text-xs text-gray-600 mt-2">
                                Justification
                              </h4>
                              <p className="text-gray-600 text-xs break-words">
                                {summary.note}
                              </p>
                            </div>
                          )}
                        </div>
                        {/* {isICD10SubmissionEnabled && (
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDiagnosis(summary.code);
                            }}
                            className="text-red-500"
                          />
                        )} */}
                      </div>
                    </div>
                  );
                })}
                {encounterDetails?.icd_10_status === 'ready' &&
                  !diagnoses?.length && (
                    <div className="text-gray-400">
                      No ICD-10 codes found for this encounter.
                    </div>
                  )}
                {encounterDetails?.icd_10_status !== 'ready' && (
                  <div className="text-gray-400">
                    Extracting ICD-10 codes...
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {/* {isICD10SubmissionEnabled && encounterStatus == 'completed' && (
        <div className="p-4 border-t border-gray-200">
          <Button
            onClick={handleSubmitToEHR}
            className="w-full"
            style={{
              backgroundColor: '#5667E9',
              borderColor: '#5667E9',
              color: 'white',
            }}
            icon={<SendOutlined />}
            loading={isSubmitting}
          >
            Submit to EHR
          </Button>
        </div>
      )} */}
      <Modal
        title="Add Custom Diagnosis"
        open={isCustomDiagnosisModalVisible}
        onOk={handleSaveCustomDiagnosis}
        onCancel={resetCustomDiagnosisModal}
        okText="Add"
        confirmLoading={isSavingCustomDiagnosis}
      >
        <div className="mb-4">
          <AutoComplete
            status={diagnosisError ? 'error' : undefined}
            options={autocompleteOptions}
            optionRender={(option) => {
              return (
                <Tooltip title={option.description} placement="right">
                  <div style={{ width: '100%', padding: '5px 0' }}>
                    {option.label}
                  </div>
                </Tooltip>
              );
            }}
            onSelect={onSelectDiagnosis}
            onSearch={debouncedSearch}
            placeholder="Search by code or description (min. 3 characters)"
            value={newDiagnosisInput}
            onChange={(value) => {
              setNewDiagnosisInput(value);
              setDiagnosisError(false);
            }}
            className="w-full"
            notFoundContent={
              isSearching
                ? 'Searching...'
                : hasMore
                  ? 'No results found'
                  : 'End of results'
            }
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            onPopupScroll={handlePopupScroll}
          />
          {diagnosisError && (
            <div className="text-red-500 text-xs mt-1">
              Field is required. Please search and select a diagnosis.
            </div>
          )}
        </div>
        <Input.TextArea
          placeholder="Add a justification..."
          value={newDiagnosisJustification}
          onChange={(e) => {
            setNewDiagnosisJustification(e.target.value);
            setJustificationError(false);
          }}
          autoSize={{ minRows: 2, maxRows: 4 }}
          className="w-full mt-0"
          status={justificationError ? 'error' : undefined}
        />
        {justificationError && (
          <div className="text-red-500 text-xs mt-1">
            Justification is required.
          </div>
        )}
      </Modal>
    </div>
  );
});
